import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import "../../styles/DashboardHome.css";
import {
  fetchSustainabilitySettingsApi,
  sendEmsTestEmailApi,
  updateSustainabilitySettingsApi,
} from "../../lib/api";

export default function SustainabilitySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [form, setForm] = useState({
    baseline_emissions_kgco2e: "",
    baseline_year: "",
    target_reduction_percent: "",
    target_year: "",
    reminders_enabled: false,
    reminder_emails: [],
    reminder_frequency: "daily",
    reminder_days_ahead: 14,
  });

  useEffect(() => {
    (async () => {
      try {
        const settings = await fetchSustainabilitySettingsApi();
        setForm({
          baseline_emissions_kgco2e: settings?.baseline_emissions_kgco2e ?? "",
          baseline_year: settings?.baseline_year ?? "",
          target_reduction_percent: settings?.target_reduction_percent ?? "",
          target_year: settings?.target_year ?? "",
          reminders_enabled: Boolean(settings?.reminders_enabled),
          reminder_emails: Array.isArray(settings?.reminder_emails) ? settings.reminder_emails : [],
          reminder_frequency: settings?.reminder_frequency || "daily",
          reminder_days_ahead: settings?.reminder_days_ahead ?? 14,
        });
      } catch (err) {
        toast.error(err?.message || "Failed to load sustainability settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addReminderEmail = () => {
    const email = String(emailInput || "").trim().toLowerCase();
    if (!email) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setForm((prev) => {
      const existing = Array.isArray(prev.reminder_emails) ? prev.reminder_emails : [];
      if (existing.includes(email)) return prev;
      return { ...prev, reminder_emails: [...existing, email] };
    });
    setEmailInput("");
  };

  const removeReminderEmail = (email) => {
    setForm((prev) => ({
      ...prev,
      reminder_emails: (prev.reminder_emails || []).filter((e) => e !== email),
    }));
  };

  const onSave = async () => {
    const baseline = Number(form.baseline_emissions_kgco2e);
    const target = Number(form.target_reduction_percent);
    const baselineYearRaw = String(form.baseline_year ?? "").trim();
    const targetYearRaw = String(form.target_year ?? "").trim();

    const baselineYear = baselineYearRaw === "" ? null : Number(baselineYearRaw);
    const targetYear = targetYearRaw === "" ? null : Number(targetYearRaw);

    if (!Number.isFinite(baseline) || baseline < 0) {
      toast.error("Baseline emissions must be a number >= 0");
      return;
    }
    if (!Number.isFinite(target) || target < 0 || target > 100) {
      toast.error("Target reduction must be between 0 and 100");
      return;
    }
    if (baselineYear !== null && (!Number.isInteger(baselineYear) || baselineYear < 1900 || baselineYear > 2200)) {
      toast.error("Baseline year must be a whole year between 1900 and 2200");
      return;
    }
    if (targetYear !== null && (!Number.isInteger(targetYear) || targetYear < 1900 || targetYear > 2200)) {
      toast.error("Target year must be a whole year between 1900 and 2200");
      return;
    }
    if (baselineYear !== null && targetYear !== null && baselineYear > targetYear) {
      toast.error("Baseline year cannot be after target year");
      return;
    }
    const reminderEmails = Array.isArray(form.reminder_emails) ? form.reminder_emails : [];
    const reminderDays = Number(form.reminder_days_ahead);
    const reminderFrequency = String(form.reminder_frequency || "").trim().toLowerCase();
    for (const e of reminderEmails) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(e || "").trim())) {
        toast.error(`Invalid reminder email: ${e}`);
        return;
      }
    }
    if (!Number.isInteger(reminderDays) || reminderDays < 1 || reminderDays > 90) {
      toast.error("Reminder days ahead must be a whole number between 1 and 90");
      return;
    }
    if (!["daily", "weekly"].includes(reminderFrequency)) {
      toast.error("Reminder frequency must be daily or weekly");
      return;
    }

    try {
      setSaving(true);
      await updateSustainabilitySettingsApi({
        baseline_emissions_kgco2e: baseline,
        baseline_year: baselineYear,
        target_reduction_percent: target,
        target_year: targetYear,
        reminders_enabled: Boolean(form.reminders_enabled),
        reminder_emails: reminderEmails,
        reminder_frequency: reminderFrequency,
        reminder_days_ahead: reminderDays,
      });
      toast.success("Sustainability settings saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save sustainability settings");
    } finally {
      setSaving(false);
    }
  };

  const onSendTestEmail = async () => {
    try {
      setSendingTest(true);
      const res = await sendEmsTestEmailApi({});
      toast.success(res?.message || "Test email sent");
    } catch (err) {
      toast.error(err?.message || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>Sustainability Settings</h1>
          <p className="subtitle">
            Configure baseline and target values used in dashboard and report indicators.
          </p>
        </div>
      </div>

      <div className="card settings-card">
        <div className="settings-header">
          <h3>Performance Inputs</h3>
          <span className="kpi-sub">These feed baseline comparison and target progress</span>
        </div>
        <div className="settings-grid">
          <label className="settings-field">
            <span>Baseline emissions (kg CO2e)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.baseline_emissions_kgco2e}
              onChange={(e) => setField("baseline_emissions_kgco2e", e.target.value)}
              disabled={loading || saving}
            />
          </label>
          <label className="settings-field">
            <span>Baseline year</span>
            <input
              type="number"
              min="1900"
              max="2200"
              step="1"
              value={form.baseline_year}
              onChange={(e) => setField("baseline_year", e.target.value)}
              disabled={loading || saving}
            />
          </label>
          <label className="settings-field">
            <span>Target reduction (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={form.target_reduction_percent}
              onChange={(e) => setField("target_reduction_percent", e.target.value)}
              disabled={loading || saving}
            />
          </label>
          <label className="settings-field">
            <span>Target year</span>
            <input
              type="number"
              min="1900"
              max="2200"
              step="1"
              value={form.target_year}
              onChange={(e) => setField("target_year", e.target.value)}
              disabled={loading || saving}
            />
          </label>
        </div>
        <div className="settings-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onSendTestEmail}
            disabled={loading || saving || sendingTest}
            style={{ marginRight: 10 }}
          >
            {sendingTest ? "Sending test..." : "Send test email"}
          </button>
          <button
            className="btn-primary"
            onClick={onSave}
            disabled={loading || saving}
          >
            {loading ? "Loading..." : saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      </div>

      <div className="card settings-card">
        <div className="settings-header">
          <h3>Email Reminders</h3>
          <span className="kpi-sub">Configure reminder delivery recipients</span>
        </div>
        <div className="settings-grid">
          <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(form.reminders_enabled)}
                onChange={(e) => setField("reminders_enabled", e.target.checked)}
                disabled={loading || saving}
              />
              Enable email reminders
            </span>
          </label>
          <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
            <span>Add recipient email</span>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="name@company.com"
                disabled={loading || saving}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addReminderEmail();
                  }
                }}
              />
              <button type="button" className="btn-secondary" onClick={addReminderEmail} disabled={loading || saving}>
                Add
              </button>
            </div>
          </label>
          <label className="settings-field">
            <span>Reminder frequency</span>
            <select
              value={form.reminder_frequency}
              onChange={(e) => setField("reminder_frequency", e.target.value)}
              disabled={loading || saving}
            >
              <option value="daily">daily</option>
              <option value="weekly">weekly</option>
            </select>
          </label>
          <label className="settings-field">
            <span>Days ahead window</span>
            <input
              type="number"
              min="1"
              max="90"
              step="1"
              value={form.reminder_days_ahead}
              onChange={(e) => setField("reminder_days_ahead", e.target.value)}
              disabled={loading || saving}
            />
          </label>
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(form.reminder_emails || []).length === 0 ? (
              <span className="kpi-sub">No reminder emails added yet.</span>
            ) : (
              (form.reminder_emails || []).map((email) => (
                <span
                  key={email}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 999,
                    padding: "6px 10px",
                    fontSize: 12,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {email}
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ padding: "2px 8px", borderRadius: 999 }}
                    onClick={() => removeReminderEmail(email)}
                    disabled={loading || saving}
                  >
                    Remove
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
        <div className="settings-actions">
          <button
            className="btn-primary"
            onClick={onSave}
            disabled={loading || saving}
          >
            {loading ? "Loading..." : saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
